import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { useNotificationBadge } from '@/components/Functional/hooks/useNotificationBadge'
import { useSwiperGestureRecovery } from '@/components/Functional/hooks/useSwiperGestureRecovery'
import { ApiError } from '@/components/Functional/lib/request'
import { buildDisplayRewards } from '@/components/Functional/utils/rewardGrouping'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { LanguageDialog } from '@/components/Layout/LanguageDialog'
import { RaundLifeCycleDebug } from '@/components/Layout/debug/RaundLifeCycleDebug'
import { DialogLoginRequired } from '@/components/Models/dialog/components/DialogLoginRequired'
import { GeofenceMessage } from '@/components/Models/geofence/GeofenceMessage'
import { CampaignComponents } from '@/components/Pages/home/components/CampaignComponents'
import { DialogCampaignDescription } from '@/components/Pages/home/components/DialogCampaignDescription'
import { DialogCampaignHowToPlay } from '@/components/Pages/home/components/DialogCampaignHowToPlay'
import { DialogFirstLoginConfirm } from '@/components/Pages/home/components/DialogFirstLoginConfirm'
import { DialogPointHistory, type PointHistoryEntry } from '@/components/Pages/home/components/DialogPointHistory'
import { DialogStampDetail } from '@/components/Pages/home/components/DialogStampDetail'
import { PointRallyCourseCard } from '@/components/Pages/home/components/PointRallyCourseCard'
import { StampSheet } from '@/components/Pages/home/components/StampSheet'
import { StampSheetSkeleton } from '@/components/Pages/home/components/StampSheetSkeleton'
import { RewardList } from '@/components/Pages/rewards/components/list/RewardList'
import { RewardListSkeleton } from '@/components/Pages/rewards/components/list/RewardListSkeleton'
import { Button, Image, Skeleton } from '@/components/UI'
import { Dialog } from '@/components/UI/Dialog'
import { Snackbar } from '@/components/UI/Snackbar'
import { Tabs } from '@/components/UI/Tabs'
import { Typography } from '@/components/UI/Typography'
import type { SheetData, SpotInStampData, StampInSheetData, UserSheetData, UserSheetHistoryData } from '@/schemas/v2'
import type { CampaignSheetMode } from '@/schemas/v2/campaign'
import type { CampaignData } from '@/schemas/v2/campaign'
import type { PopupItemData } from '@/schemas/v2/popup'
import type { SheetUnlockData } from '@/schemas/v2/sheet'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useHomeStateMachine } from './useHomeStateMachine'

const isUserSheetData = (sheet: UserSheetData | SheetData): sheet is UserSheetData =>
  'history' in sheet && Array.isArray((sheet as UserSheetData).history)

/** origin を付与して絶対URLに変換する（Stripe の success/cancel URL は絶対URL必須）。 */
function toAbsoluteUrl(path: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : (process.env.NEXT_PUBLIC_SITEURL ?? '')
  return `${origin}${path}`
}

/** ISO 日付文字列を `yyyy/MM/dd HH:mm` 形式にフォーマットする */
function formatAcquiredAt(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  const yyyy = d.getFullYear()
  const MM = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}/${MM}/${dd} ${hh}:${mm}`
}

/** スタンプ獲得履歴をポイント履歴ダイアログ用アイテムに変換する */
function buildPointHistoryItems(history: UserSheetHistoryData[], stamps: StampInSheetData[]): PointHistoryEntry[] {
  // history.point が null/undefined の場合（旧データ）はマスタ値にフォールバック
  const stampPointMap = new Map(stamps.map((s) => [s.id, s.point]))
  return history.map((h) => ({
    id: h.id,
    label: h.name,
    point: h.point ?? stampPointMap.get(h.stampId) ?? 0,
    acquiredAt: formatAcquiredAt(h.createdAt),
  }))
}

/** mappedSheets の各スタンプの型（マスタデータ＋ユーザー取得状況） */
type StampWithAcquisition = {
  id: number
  name: string
  spotId: number
  spots: SpotInStampData[]
  period: boolean
  point: number
  image: string
  overlayImage: string | null
  isHas: boolean
  hasCount: number
  stampCount: number
  earnedPoints: number
}

/** mappedSheets の各シートの型（マスタデータ＋ユーザー獲得状況） */
type SheetWithAcquisition = {
  id: number
  name: string
  type: 'stamp_rally' | 'point_rally' | 'point_card' | 'overlay' | 'none'
  description: string
  earnedPoints: number
  startAt: string
  endAt: string
  total: number
  hasCount: number
  backgroundImage: string
  isLocked: boolean
  /** 購入連動アクセス制御によるロック（#3281 COURSE_UNLOCK） */
  locked: boolean
  /** ロック解除に必要な商品情報（対象商品が無い場合は null） */
  unlock: SheetUnlockData | null
  stamps: StampWithAcquisition[]
  history: UserSheetHistoryData[]
}

/** シートの種別・backgroundImage から StampSheet に渡す campaign オブジェクトを生成する */
function buildSheetCampaignMeta(
  sheet: SheetWithAcquisition,
  campaignIsRepeatStamp: boolean | null,
  campaignStampShape: number | null,
): {
  isRepeatStamp: boolean
  stampShape: 0 | 1
  sheetMode: CampaignSheetMode
} {
  return {
    isRepeatStamp: campaignIsRepeatStamp ?? false,
    stampShape: campaignStampShape === 1 ? 1 : 0,
    sheetMode:
      sheet.type === 'overlay'
        ? 'OVERLAY'
        : sheet.type === 'stamp_rally' && sheet.backgroundImage
          ? 'BACKGROUND'
          : 'NORMAL',
  }
}

export function HomePageSkeleton() {
  const { t } = useTranslation()

  return (
    // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
    // ドキュメント全体がスクロールすると、スクロールバーがヘッダー領域まで伸びてしまう。
    <div className="flex h-dvh flex-col">
      <Header title={t('common.home', 'ホーム')} showLanguage={false} onNotification={() => {}} />
      <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]">
        <CampaignComponents
          image={''}
          period={true}
          isDisplayEventPeriod={false}
          viewStartAt={undefined}
          viewEndAt={undefined}
          showCampaignDescription={true}
          isLoading={true}
          onClickSummary={() => {}}
          onClickHowToPlay={() => {}}
        />
        <div className="mt-4 px-4">
          <div className="flex gap-4 border-b border-gray-200 pb-2">
            <Skeleton variant="text" width={90} height={20} />
            <Skeleton variant="text" width={90} height={20} />
          </div>
          {/* 読み込み完了後の StampSheet と同じ上余白（pt-4）にする */}
          <div className="pt-4">
            <StampSheetSkeleton />
          </div>
        </div>
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>
      <BottomNavigation activeItem="home" />
    </div>
  )
}

type HomePageInnerProps = {
  isAuthenticated: boolean
  campaignData: CampaignData | null
  isShowingConfirm: boolean
  homePopupItems: PopupItemData[]
  onConfirmClose: (skipNext?: boolean) => void
}

export function HomePageInner({
  isAuthenticated,
  campaignData,
  isShowingConfirm,
  homePopupItems,
  onConfirmClose,
}: HomePageInnerProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const { campaignSlug, gen, loginUrl } = useCampaignSlug()
  const api = useApi()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState(0)
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false)

  // 購入連動ロック解除（#3281）: ロック済みシート押下 → 確認ダイアログ → Stripe Checkout へ遷移
  const [pendingUnlockSheet, setPendingUnlockSheet] = useState<SheetWithAcquisition | null>(null)
  const [isSubmittingUnlock, setIsSubmittingUnlock] = useState(false)
  const [unlockErrorMessage, setUnlockErrorMessage] = useState<string | null>(null)
  const [loginOpen, setLoginOpen] = useState(false)
  // 購入後 /?session_id=... で戻った際に表示する購入完了ダイアログ（URL から派生し、閉じたら dismissed で抑止）
  const [purchaseCompleteDismissed, setPurchaseCompleteDismissed] = useState(false)

  // Stripe からのブラウザバックが bfcache（iOS Safari 等）で復元されると、送信中フラグが
  // 凍結復元されて再購入できなくなる。bfcache 復元時（persisted）は送信中フラグを解除する。
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setIsSubmittingUnlock(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const purchaseSessionId =
    router.isReady && typeof router.query.session_id === 'string' ? router.query.session_id : null
  const showPurchaseComplete = purchaseSessionId !== null && !purchaseCompleteDismissed

  // session_id から決済詳細を取得し、購入した商品名をダイアログに表示する
  const { data: purchaseDetail, isLoading: purchaseDetailLoading } = useQuery({
    queryKey: ['checkoutSessionDetail', purchaseSessionId],
    queryFn: () => api.auth.fetchCheckoutSessionDetail(purchaseSessionId as string),
    enabled: isAuthenticated && showPurchaseComplete,
  })
  const purchasedProductName = purchaseDetail?.items.map((item) => item.productName).join('、') ?? ''

  // 購入完了ダイアログを閉じる: dismissed にして URL から session_id を除去する（リロードで再表示しない）
  const handleClosePurchaseComplete = () => {
    setPurchaseCompleteDismissed(true)
    const rest = Object.fromEntries(Object.entries(router.query).filter(([key]) => key !== 'session_id'))
    router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
  }

  const { hasUnread } = useNotificationBadge(isAuthenticated)

  // 重ね絵(OVERLAY)画像は非同期ロードのため、autoHeight の初期測定では高さを取りこぼし
  // 下部がクリップされる。画像ロード後に updateAutoHeight() でスライド高さを測り直す。
  // ref は pointercancel 復帰フックと共用する（Chrome で Swiper が操作不能になる対策）
  const stampSwiperRef = useSwiperGestureRecovery()
  const pointRallySwiperRef = useSwiperGestureRecovery()

  const {
    state,
    isLoading,
    isError,
    isSummaryOpen,
    isHowToPlayOpen,
    isStampDetailOpen,
    isPointHistoryOpen,
    selectedStamp,
    sheetsData,
    rewardsData,
    pointHistoryItems,
    send: pageSend,
  } = useHomeStateMachine({
    input: {
      isAuthenticated,
    },
  })

  const isTokenRefreshWaiting = AppMachineContext.useSelector((s) => s.matches({ phase3: { tokenRefresh: 'waiting' } }))
  const prevRef = useRef({
    isTokenRefreshWaiting,
    isWaitingForRefresh: state.matches('waitingForRefresh'),
  })
  useEffect(() => {
    const prev = prevRef.current
    const isWaitingForRefresh = state.matches('waitingForRefresh')
    const current = { isTokenRefreshWaiting, isWaitingForRefresh }
    prevRef.current = current

    const tokenJustEnteredWaiting = current.isTokenRefreshWaiting && !prev.isTokenRefreshWaiting
    const stateJustEnteredWaiting = current.isWaitingForRefresh && !prev.isWaitingForRefresh

    if (
      current.isWaitingForRefresh &&
      current.isTokenRefreshWaiting &&
      (tokenJustEnteredWaiting || stateJustEnteredWaiting)
    ) {
      pageSend({ type: 'REFRESH_COMPLETE' })
    }
  }, [isTokenRefreshWaiting, state, pageSend])

  const mappedSheets: SheetWithAcquisition[] = (sheetsData?.sheets ?? []).map((sheet) => {
    const userSheet = isUserSheetData(sheet) ? sheet : null
    const history = userSheet?.history ?? []
    const historyMap = new Map(history.map((h) => [h.stampId, h]))

    const sheetHasCount =
      sheet.type === 'stamp_rally'
        ? (userSheet?.meta?.acquiredStampCount ?? 0)
        : sheet.type === 'point_card'
          ? (userSheet?.meta?.totalStampCount ?? 0)
          : 0

    const earnedPoints = sheet.type === 'point_rally' ? (userSheet?.meta?.totalPoint ?? 0) : 0

    return {
      id: sheet.id,
      name: sheet.name,
      type: sheet.type,
      description: '',
      earnedPoints,
      startAt: sheet.startAt ?? '',
      endAt: '',
      total: sheet.totalCount,
      hasCount: sheetHasCount,
      backgroundImage: sheet.backgroundImage ?? '',
      isLocked: sheet.isLocked ?? false,
      locked: sheet.locked ?? false,
      unlock: sheet.unlock ?? null,
      stamps: sheet.stamps.map((stamp) => {
        const historyEntry = historyMap.get(stamp.id)
        return {
          id: stamp.id,
          name: stamp.name,
          spotId: stamp.spots?.[0]?.id ?? 0,
          spots: stamp.spots ?? [],
          period: true,
          point: stamp.point,
          image: historyEntry?.image ?? stamp.image ?? '',
          overlayImage: historyEntry?.overlayImage ?? null,
          isHas: (historyEntry?.acquiredCount ?? 0) >= 1,
          hasCount: historyEntry?.acquiredCount ?? 0,
          stampCount: historyEntry?.acquiredCount ?? 0,
          earnedPoints: 0,
        }
      }),
      history,
    }
  })

  const handleCheckin = (spotId: number) => {
    router.push({ pathname: gen('/spots/detail'), query: { id: spotId } })
  }

  /** ロック済みシート押下: 未ログインは誘導、解除商品があれば購入確認ダイアログを開く。 */
  const handleUnlockTap = (sheet: SheetWithAcquisition) => {
    if (!sheet.unlock) return
    if (!isAuthenticated) {
      setLoginOpen(true)
      return
    }
    setUnlockErrorMessage(null)
    // 前回の送信中フラグが残っていても新しい購入操作を受け付けられるよう解除する
    setIsSubmittingUnlock(false)
    setPendingUnlockSheet(sheet)
  }

  /** 購入確認ダイアログを閉じる。送信中フラグも解除して次の購入操作を可能にする。 */
  const closeUnlockDialog = () => {
    setPendingUnlockSheet(null)
    setIsSubmittingUnlock(false)
  }

  /** 購入確認ダイアログの「購入する」押下: Checkout セッションを作成し Stripe へリダイレクトする。 */
  const handleConfirmUnlock = async () => {
    const unlock = pendingUnlockSheet?.unlock
    if (!unlock || isSubmittingUnlock) return

    setIsSubmittingUnlock(true)
    setUnlockErrorMessage(null)
    try {
      const res = await api.auth.createCheckoutSession({
        productId: unlock.productId,
        quantity: 1,
        // 購入後はホーム（/）へ戻し、session_id を検知して購入完了ダイアログを表示する。
        // {CHECKOUT_SESSION_ID} は Stripe がリダイレクト時に実セッションIDへ置換する（エンコードせず渡す）
        successUrl: `${toAbsoluteUrl(gen('/'))}?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: toAbsoluteUrl(gen('/')),
      })
      // 購入により購入連動ロックが解放されるため sheets/spots キャッシュを削除し、戻り時に最新状態を取得させる
      queryClient.removeQueries({ queryKey: ['sheets'] })
      queryClient.removeQueries({ queryKey: ['spots'] })
      // Stripe ホストの Checkout ページへリダイレクト（成功時は state を解除しない）
      window.location.assign(res.checkoutUrl)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setPendingUnlockSheet(null)
        setLoginOpen(true)
      } else {
        setUnlockErrorMessage(resolveCheckoutErrorMessage(e))
      }
      setIsSubmittingUnlock(false)
    }
  }

  /** チェックアウト作成エラーを status で出し分ける（ticketSelect と同じキーを再利用）。 */
  function resolveCheckoutErrorMessage(e: unknown): string {
    if (e instanceof ApiError) {
      switch (e.status) {
        case 400:
          return t('ec.checkout.error.unavailable', '現在ご購入いただけません（販売期間外の可能性があります）。')
        case 409:
          return t('ec.checkout.error.sold_out', '売り切れ、または購入条件を満たしていません。')
        case 503:
          return t('ec.checkout.error.service', '決済サービスに接続できません。時間をおいて再度お試しください。')
      }
    }
    return t('ec.ticket_select.checkout_error', '決済の開始に失敗しました。時間をおいて再度お試しください。')
  }

  // スタンプラリータブに表示するシート（stamp_rally / point_card / overlay）
  const stampRallySheets = mappedSheets.filter(
    (s) => s.type === 'stamp_rally' || s.type === 'point_card' || s.type === 'overlay',
  )

  // ポイントラリータブに表示するシート（point_rally のみ）
  const pointRallySheets = mappedSheets.filter((s) => s.type === 'point_rally')

  // point_rally シートが 0 件の場合はタブ 0 に強制（派生値）
  const effectiveActiveTab = pointRallySheets.length === 0 ? 0 : activeTab

  // 両タイプが存在する場合のみタブを表示する
  const showTabs = stampRallySheets.length > 0 && pointRallySheets.length > 0

  const displayRewards = useMemo(() => buildDisplayRewards(t, rewardsData?.rewards ?? []), [rewardsData, t])

  const campaignBgImage = campaignData?.backgroundImage ?? null
  const mainBackgroundStyle: CSSProperties = campaignBgImage
    ? {
        backgroundImage: `url(${campaignBgImage})`,
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% auto',
        // main 自体がスクロールコンテナになったため、既定の `scroll` では背景が
        // ビューポート1画面分に固定されてしまう。`local` でコンテンツと一緒にスクロールさせ、
        // ドキュメントスクロール時と同じ見た目（コンテンツ全長に repeat-y）を保つ。
        backgroundAttachment: 'local',
      }
    : {}

  const hasMultipleLanguages = (campaignData?.language.length ?? 0) > 1
  const showLanguageButton = hasMultipleLanguages && (campaignData?.isLocalize ?? hasMultipleLanguages)

  return (
    // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
    // ドキュメント全体がスクロールすると、スクロールバーがヘッダー領域まで伸びてしまう。
    <div className="flex h-dvh flex-col">
      <Header
        title={t('common.home', 'ホーム')}
        showLanguage={showLanguageButton}
        onLanguage={() => setIsLanguageDialogOpen(true)}
        onNotification={() => router.push(gen('/news'))}
        hasUnreadBadge={hasUnread}
      />
      <LanguageDialog
        isOpen={isLanguageDialogOpen}
        onClose={() => setIsLanguageDialogOpen(false)}
        languages={campaignData?.language ?? []}
      />
      <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]" style={mainBackgroundStyle}>
        <CampaignComponents
          image={campaignData?.image ?? ''}
          viewStartAt={campaignData?.viewStartAt}
          viewEndAt={campaignData?.viewEndAt}
          period={true}
          isDisplayEventPeriod={campaignData?.isDisplayEventPeriod ?? false}
          showCampaignDescription={true}
          isLoading={isLoading}
          onClickSummary={() => pageSend({ type: 'OPEN_SUMMARY' })}
          onClickHowToPlay={() => pageSend({ type: 'OPEN_HOW_TO_PLAY' })}
        />
        {campaignData?.homeExpansionTopImage && (
          <div className="mt-4" data-testid="home-expansion-top-image">
            <Image src={campaignData.homeExpansionTopImage} fit="width-fit" />
          </div>
        )}
        {!isAuthenticated && (
          <div className="flex justify-center mt-4 px-4">
            <Button
              label={t('home.join', '参加する')}
              fullWidth
              onClick={() => {
                location.href = `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${campaignSlug}`
              }}
            />
          </div>
        )}
        <div className="mt-4">
          {isLoading ? (
            <div className="px-4">
              <div className="flex gap-4 border-b border-gray-200 pb-2">
                <Skeleton variant="text" width={90} height={20} />
                <Skeleton variant="text" width={90} height={20} />
              </div>
              {/* 読み込み完了後の StampSheet と同じ上余白（pt-4）にする */}
              <div className="pt-4">
                <StampSheetSkeleton />
              </div>
            </div>
          ) : isError ? (
            <div className="px-4 py-8 flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-gray-600">{t('common.fetch_failed', 'データの取得に失敗しました')}</p>
              <button
                type="button"
                className="px-4 py-2 bg-main text-white rounded-full text-sm"
                onClick={() => pageSend({ type: 'RETRY' })}
              >
                {t('common.retry', '再試行')}
              </button>
            </div>
          ) : (
            <>
              {showTabs && (
                <Tabs
                  tabs={[t('home.tab.stamp_rally', 'スタンプラリー'), t('home.tab.point_rally', 'ポイントラリー')]}
                  activeIndex={effectiveActiveTab}
                  onChange={setActiveTab}
                />
              )}
              {(showTabs ? effectiveActiveTab === 0 : stampRallySheets.length > 0) && (
                <div className="px-4">
                  {stampRallySheets.length > 1 ? (
                    <>
                      <div id="stamp-sheet-pagination" className="flex justify-center pb-2 pt-4" />
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#stamp-sheet-pagination' }}
                        spaceBetween={16}
                        slidesPerView={1}
                        autoHeight
                        // ダイアログ（Mantine Modal）開閉でスライド遷移が中断され swiper.animating が
                        // true のまま stuck すると、preventInteractionOnTransition(デフォルトtrue)により
                        // ドラッグ・ドット操作が全てブロックされるため false にする（#3281 スワイプ不能対策）
                        preventInteractionOnTransition={false}
                        // Swiper は「フォーカス中の focusable 要素上で始まったドラッグ」を無視する。
                        // ダイアログを閉じると Mantine の returnFocus がロックオーバーレイ（シート全面の
                        // button）へフォーカスを戻すため、直後のスワイプが全く効かなくなる。
                        // デフォルト値から button を除外し、ボタン上でもドラッグを許可する
                        focusableElements="input, select, option, textarea, video, label"
                        onSwiper={(swiper) => {
                          stampSwiperRef.current = swiper
                        }}
                      >
                        {stampRallySheets.map((sheet) => (
                          <SwiperSlide key={sheet.id}>
                            <StampSheet
                              sheet={sheet}
                              campaign={buildSheetCampaignMeta(
                                sheet,
                                campaignData?.isRepeatStamp ?? null,
                                campaignData?.stampShape ?? null,
                              )}
                              isLocked={sheet.isLocked}
                              purchaseLocked={sheet.locked}
                              onPurchaseUnlock={() => handleUnlockTap(sheet)}
                              onStampClick={(stamp) => {
                                if (!sheet.isLocked && !sheet.locked) pageSend({ type: 'OPEN_STAMP_DETAIL', stamp })
                              }}
                              onOverlayImageLoad={() => stampSwiperRef.current?.updateAutoHeight()}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : stampRallySheets.length === 1 ? (
                    (() => {
                      const sheet = stampRallySheets[0]
                      return (
                        <div className="pt-4">
                          <StampSheet
                            sheet={sheet}
                            campaign={buildSheetCampaignMeta(
                              sheet,
                              campaignData?.isRepeatStamp ?? null,
                              campaignData?.stampShape ?? null,
                            )}
                            isLocked={sheet.isLocked}
                            purchaseLocked={sheet.locked}
                            onPurchaseUnlock={() => handleUnlockTap(sheet)}
                            onStampClick={(stamp) => {
                              if (!sheet.isLocked && !sheet.locked) pageSend({ type: 'OPEN_STAMP_DETAIL', stamp })
                            }}
                          />
                        </div>
                      )
                    })()
                  ) : null}
                </div>
              )}
              {(showTabs ? effectiveActiveTab === 1 : pointRallySheets.length > 0) && (
                <div className="px-4">
                  {pointRallySheets.length > 1 ? (
                    <>
                      <div id="point-rally-pagination" className="flex justify-center pb-2 pt-4" />
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#point-rally-pagination' }}
                        spaceBetween={16}
                        slidesPerView={1}
                        // swiper.animating が stuck しても操作をブロックしない（#3281 スワイプ不能対策）
                        preventInteractionOnTransition={false}
                        // フォーカス中の button 上でもドラッグを許可する（ロックオーバーレイ対策・stamp 側と同様）
                        focusableElements="input, select, option, textarea, video, label"
                        onSwiper={(swiper) => {
                          pointRallySwiperRef.current = swiper
                        }}
                      >
                        {pointRallySheets.map((sheet) => (
                          <SwiperSlide key={sheet.id}>
                            <PointRallyCourseCard
                              courseName={sheet.name}
                              earnedPoints={sheet.earnedPoints}
                              locked={sheet.locked}
                              onPurchaseUnlock={() => handleUnlockTap(sheet)}
                              onClickHistory={() =>
                                pageSend({
                                  type: 'OPEN_POINT_HISTORY',
                                  items: buildPointHistoryItems(sheet.history, sheet.stamps),
                                })
                              }
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : (
                    <div className="pt-4">
                      <PointRallyCourseCard
                        courseName={pointRallySheets[0].name}
                        earnedPoints={pointRallySheets[0].earnedPoints}
                        locked={pointRallySheets[0].locked}
                        onPurchaseUnlock={() => handleUnlockTap(pointRallySheets[0])}
                        onClickHistory={() =>
                          pageSend({
                            type: 'OPEN_POINT_HISTORY',
                            items: buildPointHistoryItems(pointRallySheets[0].history, pointRallySheets[0].stamps),
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {campaignData?.homeExpansionBottomImage && (
            <div className="mt-4">
              <img
                src={campaignData.homeExpansionBottomImage}
                alt=""
                className="w-full"
                data-testid="home-expansion-bottom-image"
              />
            </div>
          )}
        </div>
        {/* 特典一覧 */}
        {(rewardsData?.rewards?.length ?? 0) > 0 && (
          <div className="mt-6">
            <Typography variant="h3" className="px-4 mb-2">
              {t('bottom_navigation.reward', '特典')}
            </Typography>
            {isLoading ? <RewardListSkeleton /> : <RewardList rewards={displayRewards} />}
          </div>
        )}
        {/* 以下のダイアログはcampaignDataが読み込まれた後で表示されるので、||を使って問題ない */}
        <DialogCampaignDescription
          description={campaignData?.description || ''}
          isOpen={isSummaryOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        <DialogCampaignHowToPlay
          usage={campaignData?.usageHtml || ''}
          isOpen={isHowToPlayOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        <DialogStampDetail
          stamp={selectedStamp}
          isOpen={isStampDetailOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
          onCheckin={handleCheckin}
          stampShape={campaignData?.stampShape === 1 ? 1 : 0}
        />
        <DialogFirstLoginConfirm
          images={homePopupItems.map((p) => p.image).filter((img): img is string => img !== null)}
          campaignName={campaignData?.name || ''}
          isOpen={isShowingConfirm}
          onClose={() => onConfirmClose()}
          onConfirm={(skipNext) => onConfirmClose(skipNext)}
        />
        <DialogPointHistory
          items={pointHistoryItems}
          isOpen={isPointHistoryOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        {/* 購入連動ロック解除（#3281）の購入確認ダイアログ */}
        <Dialog
          selectorState={pendingUnlockSheet !== null}
          title={t('home.purchase_unlock.confirm_title', '購入確認')}
          closeAction={closeUnlockDialog}
        >
          <div className="flex flex-col gap-4">
            {pendingUnlockSheet?.unlock && (
              <div className="text-center">
                <p className="text-[14px] text-black">
                  {t('home.purchase_unlock.confirm_body', '「{{productName}}」を購入しますか？', {
                    productName: pendingUnlockSheet.unlock.productName,
                  })}
                </p>
                <p className="mt-1 text-[16px] font-bold text-black">
                  {t('ec.ticket_select.price', '¥{{price}}', {
                    price: pendingUnlockSheet.unlock.price.toLocaleString(),
                  })}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <Button
                label={t('home.purchase_unlock.confirm', '購入する')}
                variant="primary"
                size="md"
                fullWidth
                onClick={handleConfirmUnlock}
              />
              <Button
                label={t('home.purchase_unlock.cancel', 'キャンセル')}
                variant="system-back"
                size="md"
                fullWidth
                onClick={closeUnlockDialog}
              />
            </div>
          </div>
        </Dialog>
        {/* 購入完了ダイアログ（/?session_id=... で戻った際に表示） */}
        <Dialog
          selectorState={showPurchaseComplete}
          title={t('ec.payment_complete.title', '決済完了')}
          closeAction={handleClosePurchaseComplete}
        >
          <div className="flex flex-col gap-4 pt-2">
            <div className="text-center">
              <p className="text-[14px] text-gray-600">
                {t('ec.payment_complete.heading', 'ご購入ありがとうございます')}
              </p>
              <p className="text-[14px] text-gray-600">
                {t('ec.payment_complete.description', '決済が完了しました。購入内容は履歴からご確認いただけます。')}
              </p>
              {purchaseDetailLoading ? (
                <div className="mt-2 flex justify-center">
                  <Skeleton variant="text" width="60%" height={16} />
                </div>
              ) : (
                purchasedProductName && <p className="mt-2 text-[14px] font-bold text-black">{purchasedProductName}</p>
              )}
            </div>
            <Button
              label={t('common.close', '閉じる')}
              variant="primary"
              size="md"
              fullWidth
              onClick={handleClosePurchaseComplete}
            />
          </div>
        </Dialog>
        <DialogLoginRequired opened={loginOpen} onClose={() => setLoginOpen(false)} loginUrl={loginUrl} />
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>

      {unlockErrorMessage && (
        <div className="fixed inset-x-0 bottom-[80px] z-50 flex justify-center px-4">
          <Snackbar message={unlockErrorMessage} onClose={() => setUnlockErrorMessage(null)} />
        </div>
      )}

      {/* 入退室メッセージ（Geofence / #3259）: GPS 監視で入室・退出を検知し Snackbar 表示 */}
      <GeofenceMessage />

      <BottomNavigation activeItem="home" />
    </div>
  )
}
